import {
  AccordionGroup,
  AccordionStep,
  openItemHeading,
  DELAY_BEFORE_SCROLL,
} from "@/components/ui/Accordion";
import { FormStepAmount } from "./FormStepAmount";
import { FormStepInformation } from "./FormStepInformation";
import { useEffect, useRef } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { FormStepPayment } from "./FormStepPayment";
import { FormStepYourDetails } from "./FormStepYourDetails";
import { FormStepYourAddress } from "./FormStepYourAddress";
import { AmountLayout } from "@/layouts/AmountLayout";
import { DetailsLayout } from "@/layouts/DetailsLayout";
import { FormStepCardDetails } from "./FormStepCardDetails";
import { FormStepBankDetails } from "./FormStepBankDetails";
import { FormStepCardSelector } from "./FormStepCardSelector";
import { FormStepConfirmDetails } from "./FormStepConfirmDetails";
import { FormStepSuccess } from "./FormStepSuccess";
import { Loading } from "@/components/ui/Loading";
import type { FormProps } from "./FormProps";
import { ImperativeHandle } from "@/types/Form";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import classes from "./Form.module.scss";
import { useNavigate } from "@/hooks/useNavigate";
import { Screen, DetailsStep } from "@/types/Navigation";
import { useAppConfig } from "@/hooks/useAppConfig";
import { ErrorFallback } from "@/components/ErrorFallback";
import { useNavigationStore } from "@/stores";
import { SUBMISSION_ERROR_COPY_KEY } from "./submissionError";
import { useDonationSubmission } from "./useDonationSubmission";
import { focusAfterDOMUpdate, scrollToTop } from "@/utils/dom";

type StepRefs = {
  [key: string]: ImperativeHandle | null;
};

export function Form(props: FormProps) {
  const { id } = props;
  const t = useTranslation("_common");
  const {
    next,
    navigateTo,
    screen,
    currentDetailsStep,
    visibleDetailsSteps,
    submissionResponse,
  } = useNavigate();
  const { accordionTitles, confirmationText } = useAppConfig();

  const accordionRef = useRef<HTMLDivElement>(null);
  const cancelContinueScroll = useRef<(() => void) | undefined>(undefined);

  useEffect(() => () => cancelContinueScroll.current?.(), []);

  const stepRefs = useRef<StepRefs>({
    [DetailsStep.SelectCard]: null,
    [DetailsStep.DonationInformation]: null,
    [DetailsStep.YourDetails]: null,
    [DetailsStep.YourAddress]: null,
  });

  const findFirstInvalidStep = async (): Promise<DetailsStep | null> => {
    for (const name in stepRefs.current) {
      const ref = stepRefs.current[name];
      if (!ref) continue;

      const { isValid } = await ref.validate();

      if (!isValid) return name as DetailsStep;
    }

    return null;
  };

  const {
    processing,
    submissionError,
    showPaymentError,
    amountStepFailed,
    isRecurringPromptVisible,
    continueFromAmount,
    submit,
    confirmRecurring,
    retry,
    manageExistingRecurring,
  } = useDonationSubmission({ formId: id, findFirstInvalidStep });

  // The Continue button that had focus is in a step that just closed, or on
  // the amount screen that just unmounted. Focus follows the donor to the
  // step that opened, so a screen reader announces it. A failed validation
  // keeps its own focus target.
  const focusOpenStep = () =>
    focusAfterDOMUpdate(() => openItemHeading(accordionRef.current));

  const continueToNextStep = () => {
    cancelContinueScroll.current?.();
    next();
    const nextStep = useNavigationStore.getState().currentDetailsStep;
    // AccordionStep scrolls when first mounted; revisiting a visible step does
    // not mount it again. Continue needs the same cancellable scroll (#1179).
    if (nextStep && visibleDetailsSteps.includes(nextStep)) {
      cancelContinueScroll.current = scrollToTop(DELAY_BEFORE_SCROLL);
    }
    focusOpenStep();
  };

  const continueFromAmountToDetails = async () => {
    await continueFromAmount();
    focusOpenStep();
  };

  if (showPaymentError) {
    return <ErrorFallback />;
  }

  if (screen === Screen.Amount) {
    return (
      <AmountLayout>
        <FormStepAmount
          onStepSubmit={continueFromAmountToDetails}
          continueFailed={amountStepFailed}
        />
      </AmountLayout>
    );
  }

  if (screen === Screen.Success) {
    return <FormStepSuccess submissionResponse={submissionResponse} />;
  }

  return (
    <DetailsLayout>
      <AccordionGroup
        ref={accordionRef}
        disabled={processing}
        value={currentDetailsStep}
        onChange={(value) => {
          cancelContinueScroll.current?.();
          navigateTo(value as DetailsStep);
        }}
      >
        <AccordionStep
          title={accordionTitles.createCard}
          value={DetailsStep.SelectCard}
          visible={visibleDetailsSteps.includes(DetailsStep.SelectCard)}
          forceScroll={visibleDetailsSteps.length > 1}
        >
          <FormStepCardSelector
            ref={(ref) => {
              stepRefs.current[DetailsStep.SelectCard] = ref;
            }}
            onStepSubmit={continueToNextStep}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.donationInfo}
          value={DetailsStep.DonationInformation}
          visible={visibleDetailsSteps.includes(
            DetailsStep.DonationInformation,
          )}
          forceScroll={visibleDetailsSteps.length > 1}
        >
          <FormStepInformation
            ref={(ref) => {
              stepRefs.current[DetailsStep.DonationInformation] = ref;
            }}
            onStepSubmit={continueToNextStep}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.paymentType}
          value={DetailsStep.PaymentType}
          visible={visibleDetailsSteps.includes(DetailsStep.PaymentType)}
          forceScroll
        >
          <FormStepPayment onStepSubmit={continueToNextStep} />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.yourDetails}
          value={DetailsStep.YourDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.YourDetails)}
          forceScroll
        >
          <FormStepYourDetails
            ref={(ref) => {
              stepRefs.current[DetailsStep.YourDetails] = ref;
            }}
            onStepSubmit={continueToNextStep}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.yourAddress}
          value={DetailsStep.YourAddress}
          visible={visibleDetailsSteps.includes(DetailsStep.YourAddress)}
          forceScroll
        >
          <FormStepYourAddress
            ref={(ref) => {
              stepRefs.current[DetailsStep.YourAddress] = ref;
            }}
            onStepSubmit={continueToNextStep}
          />
        </AccordionStep>

        <AccordionStep
          title={accordionTitles.confirmDetails}
          value={DetailsStep.ConfirmDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.ConfirmDetails)}
          forceScroll
        >
          <FormStepConfirmDetails
            message={confirmationText}
            onStepSubmit={submit}
            hideSubmitButton={isRecurringPromptVisible}
          />
        </AccordionStep>

        <AccordionStep
          title={t("bank-details")}
          value={DetailsStep.BankDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.BankDetails)}
          forceScroll
        >
          <FormStepBankDetails onStepSubmit={submit} />
        </AccordionStep>

        <AccordionStep
          title={t("card-details")}
          value={DetailsStep.CardDetails}
          visible={visibleDetailsSteps.includes(DetailsStep.CardDetails)}
          forceScroll
        >
          <FormStepCardDetails
            onStepSubmit={submit}
            hideSubmitButton={isRecurringPromptVisible}
          />
        </AccordionStep>
      </AccordionGroup>

      {submissionError && (
        <div className={classes.pageError}>
          <Alert variant="error">
            {t(SUBMISSION_ERROR_COPY_KEY[submissionError])}
          </Alert>

          {submissionError !== "charged-pending-review" && (
            <div className={classes.pageErrorActions}>
              <Button onClick={retry} disabled={processing}>
                {t("try-again")}
              </Button>
            </div>
          )}
        </div>
      )}

      {isRecurringPromptVisible && (
        <div className={classes.recurringPrompt}>
          <Alert variant="error">
            You already have a recurring donation set with us. Do you want to
            manage that donation or set up a new one?
          </Alert>
          <div className={classes.recurringPromptActions}>
            <Button onClick={manageExistingRecurring}>Manage existing</Button>
            <Button variant="outlined" onClick={confirmRecurring}>
              Continue
            </Button>
          </div>
        </div>
      )}

      {processing && (
        <div>
          <Loading message="Donation processing" />
        </div>
      )}
    </DetailsLayout>
  );
}
